import { useCallback, useEffect, useRef, useState } from "react";
import { CheckRounded } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, Button, CircularProgress, Stack, TextField, Typography as T } from "@mui/material";
import useDataset from "datasets/hooks/useDataset";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import useErrorToast from "~/contexts/error-toast";
import { trackEvent } from "~/utils/monitoring";

const { VITE_ANALYSIS_AGENT_API } = import.meta.env;

const MAX_LENGTH = 50_000;

type FetchState = "loading" | "ready" | "error";
type SaveState = "idle" | "saving" | "saved";

const AgentInstructionsEditor = () => {
  const { data: dataset } = useDataset();
  const { showError } = useErrorToast();
  const isOwner = dataset?.access_level === DatasetAccessLevel.OWNER;

  const [fetchState, setFetchState] = useState<FetchState>("loading");
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [instructions, setInstructions] = useState("");
  const savedRef = useRef("");
  const abortRef = useRef<AbortController | null>(null);

  const isDirty = instructions !== savedRef.current;

  const fetchInstructions = useCallback(
    async (datasetId: string) => {
      setFetchState("loading");
      try {
        const resp = await fetch(
          `${VITE_ANALYSIS_AGENT_API}/datasets/${datasetId}/agent-instructions`
        );

        if (resp.status === 404) {
          // No instructions exist yet — start with empty
          savedRef.current = "";
          setInstructions("");
          setFetchState("ready");
          return;
        }

        if (!resp.ok) {
          throw new Error("Failed to fetch agent instructions");
        }

        const data = await resp.json();
        const text = data.agent_instructions ?? "";
        savedRef.current = text;
        setInstructions(text);
        setFetchState("ready");
      } catch (e) {
        console.error(e);
        setFetchState("error");
        showError("Unable to load agent instructions");
      }
    },
    [showError]
  );

  useEffect(() => {
    if (dataset?.id) {
      fetchInstructions(dataset.id);
    }
  }, [dataset?.id, fetchInstructions]);

  const handleSave = async () => {
    if (!dataset || !isDirty) {
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setSaveState("saving");

    try {
      const resp = await fetch(
        `${VITE_ANALYSIS_AGENT_API}/datasets/${dataset.id}/agent-instructions`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ agent_instructions: instructions }),
          signal: controller.signal,
        }
      );

      if (!resp.ok) {
        throw new Error("Failed to save agent instructions");
      }

      savedRef.current = instructions;
      setSaveState("saved");

      trackEvent("dataset_agent_instructions_saved", {
        dataset_id: dataset.id,
        length: instructions.length,
      });
    } catch (e) {
      if ((e as Error).name === "AbortError") {
        return;
      }
      console.error(e);
      setSaveState("idle");
      showError("Unable to save agent instructions");
    }
  };

  const handleRevert = () => {
    setInstructions(savedRef.current);
    setSaveState("idle");
  };

  const handleChange = (value: string) => {
    setInstructions(value);
    setSaveState("idle");
  };

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  if (fetchState === "loading") {
    return (
      <Stack alignItems="center" py={4}>
        <CircularProgress size={24} />
      </Stack>
    );
  }

  if (fetchState === "error") {
    return (
      <Stack gap={1} py={2}>
        <T variant="body2" color="error">
          Failed to load agent instructions.
        </T>
        <Box>
          <Button
            size="small"
            variant="outlined"
            onClick={() => dataset?.id && fetchInstructions(dataset.id)}
          >
            Retry
          </Button>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack gap={2}>
      <Box>
        <T variant="body2" color="text.secondary">
          Help the Analysis Agent understand your data better. Add any context that might be useful,
          such as how the data is structured, known quirks, preferred analysis approaches, or
          relevant domain knowledge. This will be included in every conversation.
        </T>
      </Box>
      <TextField
        fullWidth
        multiline
        minRows={6}
        maxRows={20}
        placeholder="e.g. This dataset uses NAACCR coding standards. The 'site' column contains ICD-O-3 topography codes. Exclude rows where status = 'withdrawn' unless explicitly asked."
        value={instructions}
        disabled={!isOwner}
        onChange={(e) => handleChange(e.target.value)}
        inputProps={{ maxLength: MAX_LENGTH }}
        size="small"
      />
      <Stack direction="row" alignItems="center" gap={1}>
        <T variant="caption" color="text.secondary">
          {instructions.length.toLocaleString()} / {MAX_LENGTH.toLocaleString()}
        </T>
        {saveState === "saved" && !isDirty && (
          <Stack direction="row" alignItems="center" gap={0.5}>
            <CheckRounded sx={{ fontSize: 16 }} color="success" />
            <T variant="caption" color="success.main">
              Saved
            </T>
          </Stack>
        )}
      </Stack>
      {isOwner && (
        <Stack direction="row" gap={1}>
          <LoadingButton
            variant="contained"
            size="small"
            loading={saveState === "saving"}
            disabled={!isDirty}
            onClick={handleSave}
          >
            Save
          </LoadingButton>
          <Button variant="outlined" size="small" disabled={!isDirty} onClick={handleRevert}>
            Revert
          </Button>
        </Stack>
      )}
    </Stack>
  );
};

export default AgentInstructionsEditor;
